import React, { ReactNode, useMemo, useCallback } from 'react';
import {
  MecColour,
  MecImage,
  MecProduct,
  MecSize,
  MecVariant,
  PriceGroup,
  PriceType,
  PromoBadge,
  BrandProduct,
  Breadcrumbs,
  AvailabilityStatus,
  ProductTag,
} from '@adapters/bigcommerce/types/product';
import { MultiGroupPrices } from '@adapters/bigcommerce/operations/getAllCustomerGroupPrices';
import { isWithinDateRangePST } from '@utils/dateAndTime/isBetween';
import {
  FEATURE_FLAG_PDP_MIN_PURCHASE_QUANTITY,
  FEATURE_FLAG_MEMBER_PRICING,
  FEATURE_FLAG_PRODUCT_TAGS_FROM_METAFIELD,
} from '@config/feature-flag';
import useCustomer from '@adapters/bigcommerce/hooks/customer/useCustomer';
import type { PriceSelectionResult } from '@utils/selectPriceByGroup';
import {
  selectPriceByGroupWithMetadata,
  getPriceListInfo,
} from '@utils/selectPriceByGroup';
import {
  PdpShopperGeolocation,
  usePdpFindInStorePreview,
} from './usePdpFindInStorePreview';

export interface ReducerState {
  selectedColourId: number | null;
  selectedSizeId: number | null;
  quantity: number | null;
  addToCartClicked: boolean;
}

export interface DerivedState {
  entityId?: number;
  name?: string;
  gaName?: string;
  erpCategory?: string;
  brand?: BrandProduct | null;
  selectedColour?: MecColour;
  selectedSize?: MecSize;
  selectedVariant?: MecVariant;
  selectedAvailability: boolean;
  shippingRestrictions: MecProduct['shippingRestrictions'] | null;
  categories?: string[];
  categoryTargets?: string[];
  colourOptionId?: number | null;
  sizeOptionId?: number | null;
  variants?: MecVariant[];
  images?: MecImage[];
  isGiftCard: boolean;
  sizes?: Array<
    MecSize & {
      notAvailableForSelectedColour?: boolean;
      isColourSelected?: boolean;
    }
  >;
  colours?: Array<MecColour & { notAvailableForSelectedSize?: boolean }>;
  price: PriceGroup | null;
  promoBadge: PromoBadge | null;
  productCode: string;
  breadcrumbs?: Breadcrumbs[];
  availabilityStatus?: AvailabilityStatus;
  hasInCartDiscountTag?: boolean;
  tagInfoModalPath?: string;
  description?: string;
  inCartDiscount?: boolean;
  IS_ALGONOMY_PATH_CAT_ID_ENABLED?: boolean;
  isCanadianBrand?: boolean;
  madeInCanada?: boolean;
  hasComparison?: boolean;
  minPurchaseQuantity: number | null;
  customerGroupPrices?: MultiGroupPrices | null;
  actualPriceList: PriceSelectionResult;
  hasMemberPriceDifference: boolean;
  shouldShowGuestPriceComparison: boolean;
  hasCustomerGroupPriceDifference: boolean;
  productTags?: ProductTag[];
  shopperGeolocation: PdpShopperGeolocation | null;
}

export interface Actions {
  setSelectedColourId: (colourId: number) => void;
  setSelectedSizeId: (sizeId: number | null) => void;
  setAddToCartClicked: (addToCartClicked: boolean) => void;
  setQuantity: (quantity: number | null) => void;
  resetStateWithSizeAndColour: (itemSize: number, itemColour: number) => void;
  setShopperGeolocation: (location: PdpShopperGeolocation) => void;
}

export type PdpState = ReducerState & DerivedState & Actions;

type Action =
  | {
      type: 'SET_SELECTED_COLOUR';
      selectedColour: number;
    }
  | {
      type: 'SET_SELECTED_SIZE';
      selectedSize: number | null;
    }
  | {
      type: 'ADD_TO_CART_CLICKED';
      addToCartClicked: boolean;
    }
  | {
      type: 'SET_QUANTITY';
      quantity: number | null;
    }
  | {
      type: 'RESET_STATE_WITH_SIZE_AND_COLOUR';
      itemSize: number;
      itemColour: number;
    };

const createInitialState = (
  product?: MecProduct,
  IS_ALGONOMY_PATH_CAT_ID_ENABLED?: boolean,
  initialColourId?: number | null
) => {
  // Metafield-sourced product tags are editorial (e.g. "Staff Pick"), not price
  // discounts, so they must not drive the in-cart-discount price treatment.
  const inCartDiscount = FEATURE_FLAG_PRODUCT_TAGS_FROM_METAFIELD
    ? undefined
    : product?.productTags?.find(
        (item) => !item?.isLink && item?.variants.length === 0
      );

  return {
    selectedColourId: initialColourId ?? null,
    selectedSizeId: product?.sizes.length === 1 ? product.sizes[0].id : null,
    selectedAvailability: false,
    shippingRestrictions: product?.shippingRestrictions ?? null,
    quantity:
      FEATURE_FLAG_PDP_MIN_PURCHASE_QUANTITY && product?.minPurchaseQuantity
        ? product?.minPurchaseQuantity
        : 1,
    addToCartClicked: false,
    isGiftCard: false,
    setSelectedColourId: () => null,
    setSelectedSizeId: () => null,
    setAddToCartClicked: () => null,
    setQuantity: () => null,
    resetStateWithSizeAndColour: () => null,
    shopperGeolocation: null,
    setShopperGeolocation: () => null,
    price: null,
    promoBadge: null,
    productCode: '',
    AvailabilityStatus: product?.availabilityStatus,
    tagInfoModalPath: inCartDiscount?.path,
    IS_ALGONOMY_PATH_CAT_ID_ENABLED,
    minPurchaseQuantity:
      FEATURE_FLAG_PDP_MIN_PURCHASE_QUANTITY && product?.minPurchaseQuantity
        ? product?.minPurchaseQuantity
        : null,
    customerGroupPrices: null,
    actualPriceList: {
      price: null,
      groupKey: 'guest' as const,
      hasFallback: false,
      source: 'not-found' as const,
    },
    hasMemberPriceDifference: false,
    shouldShowGuestPriceComparison: false,
    hasCustomerGroupPriceDifference: false,
    productTags: product?.productTags ?? [],
  };
};

export const PdpContext = React.createContext<PdpState>(createInitialState());
PdpContext.displayName = 'PdpContext';

const cartUiReducer = (state: ReducerState, action: Action) => {
  switch (action.type) {
    case 'SET_SELECTED_COLOUR': {
      return {
        ...state,
        selectedColourId: action.selectedColour,
      };
    }
    case 'SET_SELECTED_SIZE': {
      if (state.selectedSizeId === action.selectedSize) {
        return state;
      }
      return {
        ...state,
        selectedSizeId: action.selectedSize,
      };
    }
    case 'ADD_TO_CART_CLICKED': {
      return {
        ...state,
        addToCartClicked: action.addToCartClicked,
      };
    }
    case 'SET_QUANTITY': {
      return {
        ...state,
        quantity: action.quantity,
      };
    }
    case 'RESET_STATE_WITH_SIZE_AND_COLOUR': {
      return {
        ...state,
        selectedColourId: action.itemColour ?? null,
        selectedSizeId: action.itemSize ?? null,
        selectedAvailability: false,
        quantity: 1,
        addToCartClicked: false,
        isGiftCard: false,
        price: null,
        promoBadge: null,
        productCode: '',
      };
    }
    default: {
      return { ...state };
    }
  }
};

export interface PdpProviderProps {
  product: MecProduct;
  productCode: string;
  children: ReactNode;
  IS_ALGONOMY_PATH_CAT_ID_ENABLED: boolean;
  hasComparison: boolean;
  customerGroupPrices: MultiGroupPrices | null;
  initialColourId?: number | null;
}

export const PdpContextProvider = ({
  children,
  product,
  productCode,
  IS_ALGONOMY_PATH_CAT_ID_ENABLED,
  hasComparison,
  customerGroupPrices,
  initialColourId,
}: PdpProviderProps) => {
  const { shopperGeolocation, setShopperGeolocationStable } =
    usePdpFindInStorePreview({ isGiftCard: Boolean(product?.isGiftCard) });

  const { customer } = useCustomer();
  const currentPriceList = useMemo(
    () => getPriceListInfo(customer?.customerGroupId),
    [customer?.customerGroupId]
  );

  const [state, dispatch] = React.useReducer(
    cartUiReducer,
    { product, IS_ALGONOMY_PATH_CAT_ID_ENABLED, initialColourId },
    (arg) =>
      createInitialState(
        arg.product,
        arg.IS_ALGONOMY_PATH_CAT_ID_ENABLED,
        arg.initialColourId
      )
  );

  const setSelectedColourId = useCallback(
    (colourId: number) => {
      dispatch({ type: 'SET_SELECTED_COLOUR', selectedColour: colourId });

      // If current selected size not available for colour, then set to null
      if (
        state.selectedSizeId &&
        !product.variants.find(
          (variant) =>
            variant.colourId === colourId &&
            variant.sizeId === state.selectedSizeId &&
            variant.hasStockAvailable
        )
      ) {
        dispatch({ type: 'SET_SELECTED_SIZE', selectedSize: null });
      }
    },
    [state.selectedSizeId, product]
  );

  const setSelectedSizeId = (sizeId: number | null) =>
    dispatch({ type: 'SET_SELECTED_SIZE', selectedSize: sizeId });

  const setAddToCartClicked = useCallback((addToCartClicked: boolean) => {
    dispatch({
      type: 'ADD_TO_CART_CLICKED',
      addToCartClicked,
    });
  }, []);

  const setQuantity = (quantity: number | null) =>
    dispatch({ type: 'SET_QUANTITY', quantity });

  const resetStateWithSizeAndColour = (itemSize: number, itemColour: number) =>
    dispatch({
      type: 'RESET_STATE_WITH_SIZE_AND_COLOUR',
      itemSize,
      itemColour,
    });

  const selectedColour = useMemo(() => {
    if (!state.selectedColourId) return undefined;

    return product.colours.find(
      (colour) => colour.id === state.selectedColourId
    );
  }, [state.selectedColourId, product?.colours]);

  const selectedSize = useMemo(() => {
    if (!state.selectedSizeId) return undefined;

    return product.sizes.find((size) => size.id === state.selectedSizeId);
  }, [state.selectedSizeId, product]);

  const selectedVariant = useMemo(() => {
    if (
      !state.selectedColourId ||
      (product.sizes?.length && !state.selectedSizeId)
    )
      return undefined;

    return product.variants.find(
      (variant) =>
        variant.colourId === state.selectedColourId &&
        (!product.sizes?.length || variant.sizeId === state.selectedSizeId)
    );
  }, [state, product]);

  const selectedPrice = useMemo(() => {
    if (selectedVariant) return selectedVariant.price;
    if (selectedColour) return selectedColour.price;

    return product?.price ?? null;
  }, [selectedVariant, selectedColour, product]);

  const selectedAvailability = useMemo(() => {
    if (selectedVariant) return selectedVariant.availableForSaleOnline;
    if (selectedColour) return selectedColour.availableForSaleOnline;

    return product?.availableForSaleOnline ?? false;
  }, [selectedVariant, selectedColour, product]);

  const sizesForColour = useMemo(() => {
    if (!state.selectedColourId) return product?.sizes;

    const availableSizeIds = product.variants.flatMap((variant) => {
      if (
        variant.colourId === state.selectedColourId &&
        variant.hasStockAvailable
      ) {
        return variant.sizeId;
      }

      return [];
    });

    const isColourSelected = product.variants.flatMap((variant) => {
      if (variant.colourId === state.selectedColourId) {
        return variant.sizeId;
      }

      return [];
    });

    return product.sizes.map((size) => ({
      ...size,
      notAvailableForSelectedColour: product?.isGiftCard
        ? false
        : !availableSizeIds.includes(size.id),
      isColourSelected: isColourSelected.includes(size.id),
    }));
  }, [state, product]);

  const coloursForSize = useMemo(() => {
    if (!state.selectedSizeId) return product?.colours;

    const availableColourIds = product.variants.flatMap((variant) => {
      if (
        variant.sizeId === state.selectedSizeId &&
        variant.hasStockAvailable
      ) {
        return variant.colourId;
      }

      return [];
    });

    return product?.colours?.map((colour) => ({
      ...colour,
      notAvailableForSelectedSize: product?.isGiftCard
        ? false
        : !availableColourIds.includes(colour.id),
    }));
  }, [
    state.selectedSizeId,
    product?.colours,
    product.variants,
    product?.isGiftCard,
  ]);

  // Check if member has different price than guest
  const hasMemberPriceDifference = useMemo(() => {
    if (!customerGroupPrices || !FEATURE_FLAG_MEMBER_PRICING) {
      return false;
    }

    const memberPrice = selectedVariant
      ? customerGroupPrices.member?.variants.find(
          (v) => v.entityId === selectedVariant.entityId
        )?.price
      : customerGroupPrices.member?.price;

    const guestPrice = selectedVariant
      ? customerGroupPrices.guest?.variants.find(
          (v) => v.entityId === selectedVariant.entityId
        )?.price
      : customerGroupPrices.guest?.price;

    if (!memberPrice || !guestPrice) {
      return false;
    }

    // Compare sale/clearance prices
    const memberValue = memberPrice.price?.value ?? memberPrice.lowPrice?.value;
    const guestValue = guestPrice.price?.value ?? guestPrice.lowPrice?.value;

    // Compare base (regular) prices
    const memberBaseValue =
      memberPrice.basePrice?.value ?? memberPrice.baseLowPrice?.value;
    const guestBaseValue =
      guestPrice.basePrice?.value ?? guestPrice.baseLowPrice?.value;

    return memberValue !== guestValue || memberBaseValue !== guestBaseValue;
  }, [customerGroupPrices, selectedVariant]);

  const inCartDiscount = useMemo(() => {
    // Metafield-sourced product tags are editorial (e.g. "Staff Pick"), not price
    // discounts. They share the "non-link, no-variant" shape that the check below
    // treats as an in-cart discount, so never derive a discount from them.
    if (FEATURE_FLAG_PRODUCT_TAGS_FROM_METAFIELD) {
      return false;
    }
    let hasDiscount = false;
    product?.productTags?.forEach((tag) => {
      if (tag?.start_date && tag?.end_date) {
        const isValidDates = isWithinDateRangePST(
          tag?.start_date,
          tag?.end_date
        );
        if (!isValidDates) {
          return;
        }
      }
      if (
        (!tag?.isLink &&
          tag?.colours &&
          tag.colours.length !== 0 &&
          tag.colours.includes(selectedColour?.id as never)) ||
        (!tag?.isLink &&
          tag?.variants &&
          tag.variants.length !== 0 &&
          typeof selectedVariant?.sku === 'string' &&
          tag.variants.includes(selectedVariant.sku as never))
      ) {
        hasDiscount = true;
      }
      if (!tag?.isLink && tag?.variants.length === 0) {
        hasDiscount = true;
      }
    });

    return hasDiscount;
  }, [product?.productTags, selectedColour?.id, selectedVariant?.sku]);

  // Only show colours that have at least one available variant (not marketplace, not OOS)
  const availableColourIds = (product.variants || [])
    .filter((variant) => variant.hasStockAvailable)
    .map((variant) => variant.colourId);

  const filteredColours = (coloursForSize || []).filter((colour) =>
    availableColourIds.includes(colour.id)
  );

  // Compute actual price list with metadata about what's being used
  const actualPriceList = useMemo(() => {
    return selectPriceByGroupWithMetadata(
      customerGroupPrices ?? null,
      currentPriceList.customerGroupId,
      selectedVariant?.entityId
    );
  }, [customerGroupPrices, currentPriceList.customerGroupId, selectedVariant]);

  // ED-468: `hasMemberPriceDifference` above is a product data fact ("does a
  // member deal exist"), intentionally left unguarded — it also drives the
  // guest-facing membership-marketing deals banner, which should show
  // regardless of viewer. The rendered badge itself must additionally check
  // the resolved viewer group, so a guest never sees the member-only badge.
  const promoBadge = useMemo(() => {
    const source = selectedVariant ?? selectedColour;
    const badge = source
      ? hasMemberPriceDifference &&
        FEATURE_FLAG_MEMBER_PRICING &&
        actualPriceList.groupKey !== 'guest'
        ? source.promoBadgeMember
        : source.promoBadgeGuest
      : null;
    return badge
      ? { ...badge, isMemberBadge: badge.is_member_price ?? false }
      : null;
  }, [
    selectedColour,
    selectedVariant,
    hasMemberPriceDifference,
    actualPriceList.groupKey,
  ]);

  // Show guest price as original (crossed out) when viewing any non-guest group price AND guest > current group price
  const shouldShowGuestPriceComparison = useMemo(() => {
    if (!customerGroupPrices || !FEATURE_FLAG_MEMBER_PRICING) {
      return false;
    }

    const guestPrice = selectedVariant
      ? customerGroupPrices.guest?.variants.find(
          (v) => v.entityId === selectedVariant.entityId
        )?.price
      : customerGroupPrices.guest?.price;

    if (!guestPrice || !actualPriceList.price) {
      return false;
    }

    const guestValue = guestPrice.price?.value ?? guestPrice.lowPrice?.value;
    const currentGroupValue =
      actualPriceList.price.price?.value ??
      actualPriceList.price.lowPrice?.value;

    if (guestValue == null || currentGroupValue == null) {
      return false;
    }

    return (
      actualPriceList.groupKey !== 'guest' && guestValue > currentGroupValue
    );
  }, [customerGroupPrices, selectedVariant, actualPriceList]);

  // True when the displayed price (current group) differs from guest — show label + green price only then (match cart)
  const hasCustomerGroupPriceDifference = useMemo(() => {
    if (!customerGroupPrices || !FEATURE_FLAG_MEMBER_PRICING) {
      return false;
    }
    if (
      actualPriceList.groupKey === 'guest' ||
      actualPriceList.groupKey === 'member'
    ) {
      return false;
    }
    const guestPrice = selectedVariant
      ? customerGroupPrices.guest?.variants.find(
          (v) => v.entityId === selectedVariant.entityId
        )?.price
      : customerGroupPrices.guest?.price;
    if (!guestPrice || !actualPriceList.price) {
      return false;
    }
    const guestValue = guestPrice.price?.value ?? guestPrice.lowPrice?.value;
    const currentGroupValue =
      actualPriceList.price.price?.value ??
      actualPriceList.price.lowPrice?.value;
    if (guestValue == null || currentGroupValue == null) {
      return false;
    }
    return guestValue !== currentGroupValue;
  }, [customerGroupPrices, selectedVariant, actualPriceList]);

  // Use customer-specific price if available and has displayable value, otherwise fall back to SSR price
  const hasDisplayablePrice = (p: PriceGroup | null) => {
    if (!p) return false;
    const v = p.price?.value ?? p.lowPrice?.value ?? p.highPrice?.value;
    return typeof v === 'number';
  };
  // When a colour is selected, and all its variants share the same price for the
  // current customer group, scope the PDP price to that colour instead of using
  // the aggregated low/high range across all colours.
  const colourScopedPrice = useMemo(() => {
    if (
      !customerGroupPrices ||
      !selectedColour ||
      !actualPriceList.price ||
      !hasDisplayablePrice(actualPriceList.price)
    ) {
      return actualPriceList.price;
    }

    const groupKey = actualPriceList.groupKey;
    const groupData = customerGroupPrices[groupKey];
    if (!groupData) return actualPriceList.price;

    // When a specific variant is selected, return its customer-group price directly
    // rather than a colour-scoped range.
    if (selectedVariant?.entityId != null) {
      const variantGroupPrice = groupData.variants.find(
        (v) => v.entityId === selectedVariant.entityId
      );
      if (variantGroupPrice) {
        return variantGroupPrice.price;
      }
      // Variant not found in this group's data — log a warning and fall back to product-level price
      // This should not normally happen; pricing data should include all selectable variants.
      // If you see this frequently, investigate the customer-group pricing data pipeline.
      console.warn(
        '[PdpContext] Missing customer group pricing for selected variant.',
        {
          productId: product?.entityId,
          groupKey,
          selectedVariantId: selectedVariant.entityId,
          selectedColourId: selectedColour?.id,
        }
      );
      return actualPriceList.price;
    }

    const colourVariantIds = product.variants
      .filter((variant) => variant.colourId === selectedColour.id)
      .map((variant) => variant.entityId)
      .filter((id): id is number => typeof id === 'number');

    // Compute colour-scoped variant price groups up front so they can be used
    // for both the single-price and multi-price cases below.
    const colourVariantPriceGroups = groupData.variants
      .filter((v) => colourVariantIds.includes(v.entityId))
      .map((v) => v.price)
      .filter((p): p is PriceGroup => p != null);

    const perColourPrices = colourVariantPriceGroups
      .map((p) => p.price?.value ?? p.lowPrice?.value ?? null)
      .filter((v): v is number => v != null);

    const uniquePrices = Array.from(new Set(perColourPrices));

    const basePrice = actualPriceList.price;
    const currencySource =
      basePrice.price ?? basePrice.lowPrice ?? basePrice.highPrice;

    if (!currencySource) {
      return actualPriceList.price;
    }

    const currencyCode = currencySource.currencyCode;

    // Derive colour-specific priceType from this colour's variants instead of
    // inheriting the product-level value, which may reflect other colours (e.g.
    // a clearance colour making the whole product priceType=Clearance).
    // Sale > Clearance > Regular — resolved in a single pass.
    let colourPriceType = PriceType.Regular;
    for (const p of colourVariantPriceGroups) {
      if (p.priceType === PriceType.Sale) {
        colourPriceType = PriceType.Sale;
        break;
      }
      if (p.priceType === PriceType.Clearance) {
        colourPriceType = PriceType.Clearance;
      }
    }

    // Derive epochEndDate/isoEndDate from the colour's variants rather than
    // inheriting the product-level value via the ...basePrice spread.
    // A sale colour may set these on the product-level price, which would
    // incorrectly show a countdown timer when a regular/clearance colour is selected.
    // Only carry the end date when this colour is actually on sale.
    let colourEpochEndDate: number | null = null;
    let colourIsoEndDate: string | null = null;
    if (colourPriceType === PriceType.Sale) {
      const saleVariant = colourVariantPriceGroups.find(
        (p) => p.priceType === PriceType.Sale
      );
      colourEpochEndDate = saleVariant?.epochEndDate ?? null;
      colourIsoEndDate = saleVariant?.isoEndDate ?? null;
    }

    const colourBaseValues = colourVariantPriceGroups
      .map((p) => p.basePrice?.value ?? null)
      .filter((v): v is number => v != null);
    const uniqueBaseValues = Array.from(new Set(colourBaseValues));

    // Used as a fallback base-price range whenever sizes don't share a single
    // base price — regardless of whether the current price is uniform or not.
    const minBase = colourBaseValues.length
      ? Math.min(...colourBaseValues)
      : null;
    const maxBase = colourBaseValues.length
      ? Math.max(...colourBaseValues)
      : null;

    if (uniquePrices.length !== 1) {
      // Multiple price points within this colour (e.g. sizes priced differently).
      // Build a colour-scoped range instead of falling back to the product-level
      // price which spans all colours and may carry an incorrect priceType/range.
      if (uniquePrices.length === 0) {
        return actualPriceList.price;
      }

      const minPrice = Math.min(...uniquePrices);
      const maxPrice = Math.max(...uniquePrices);

      const colourScopedRangePrice = {
        ...basePrice,
        priceType: colourPriceType,
        epochEndDate: colourEpochEndDate,
        isoEndDate: colourIsoEndDate,
        price: null,
        lowPrice: { value: minPrice, currencyCode },
        highPrice: { value: maxPrice, currencyCode },
        // basePrice is set only when all sizes share the same base price.
        // When sizes have differing base prices, baseLowPrice/baseHighPrice carry
        // the range and basePrice is null — matching the shape used by useFormattedPrice.
        basePrice:
          uniqueBaseValues.length === 1
            ? { value: uniqueBaseValues[0], currencyCode }
            : null,
        baseLowPrice:
          minBase !== null ? { value: minBase, currencyCode } : undefined,
        baseHighPrice:
          maxBase !== null ? { value: maxBase, currencyCode } : undefined,
      };

      return colourScopedRangePrice;
    }

    const colourBasePrice =
      uniqueBaseValues.length === 1
        ? { value: uniqueBaseValues[0], currencyCode }
        : null;

    const colourScopedSinglePrice = {
      ...basePrice,
      priceType: colourPriceType,
      epochEndDate: colourEpochEndDate,
      isoEndDate: colourIsoEndDate,
      price: {
        value: uniquePrices[0],
        currencyCode,
      },
      lowPrice: undefined,
      highPrice: undefined,
      basePrice: colourBasePrice,
      // When sizes share one current price but differ in base price (e.g. a
      // uniform clearance price over size-scaled regular pricing), basePrice
      // above is null — fall back to a base-price range instead of leaving
      // these undefined, which renders as a literal "null - null" string.
      baseLowPrice:
        colourBasePrice == null && minBase !== null
          ? { value: minBase, currencyCode }
          : undefined,
      baseHighPrice:
        colourBasePrice == null && maxBase !== null
          ? { value: maxBase, currencyCode }
          : undefined,
    };

    return colourScopedSinglePrice;
  }, [
    customerGroupPrices,
    selectedColour,
    actualPriceList.price,
    actualPriceList.groupKey,
    selectedVariant?.entityId,
    product.variants,
    product?.entityId,
  ]);

  const computedPrice =
    colourScopedPrice && hasDisplayablePrice(colourScopedPrice)
      ? colourScopedPrice
      : selectedPrice;

  const value = useMemo(
    () => ({
      ...state,
      setSelectedColourId,
      setSelectedSizeId,
      setAddToCartClicked,
      setQuantity,
      resetStateWithSizeAndColour,
      shopperGeolocation,
      setShopperGeolocation: setShopperGeolocationStable,
      selectedColour,
      selectedSize,
      selectedVariant,
      selectedAvailability,
      inCartDiscount,
      entityId: product?.entityId,
      categories: product?.categories,
      categoryTargets: product?.categoryTargets,
      colours: filteredColours,
      colourOptionId: product?.colourOptionId,
      shippingRestrictions: product?.shippingRestrictions,
      sizes: sizesForColour,
      sizeOptionId: product?.sizeOptionId,
      variants: product?.variants,
      images: product?.images,
      promoBadge,
      price: FEATURE_FLAG_MEMBER_PRICING ? computedPrice : selectedPrice,
      isGiftCard: product?.isGiftCard,
      name: product?.name,
      gaName: product?.gaName,
      erpCategory: product?.erpCategory,
      brand: product?.brand,
      breadcrumbs: product?.breadcrumbs,
      productCode,
      availabilityStatus: product?.availabilityStatus,
      description: product?.description,
      isCanadianBrand: product?.isCanadianBrand,
      madeInCanada: product?.madeInCanada,
      hasComparison: hasComparison,
      minPurchaseQuantity:
        FEATURE_FLAG_PDP_MIN_PURCHASE_QUANTITY && product?.minPurchaseQuantity
          ? product?.minPurchaseQuantity
          : null,
      customerGroupPrices,
      actualPriceList,
      hasMemberPriceDifference,
      shouldShowGuestPriceComparison,
      hasCustomerGroupPriceDifference,
    }),
    [
      state,
      product,
      productCode,
      promoBadge,
      selectedVariant,
      selectedColour,
      selectedSize,
      computedPrice,
      selectedPrice,
      selectedAvailability,
      sizesForColour,
      filteredColours,
      setSelectedColourId,
      setAddToCartClicked,
      setShopperGeolocationStable,
      shopperGeolocation,
      inCartDiscount,
      hasComparison,
      customerGroupPrices,
      actualPriceList,
      hasMemberPriceDifference,
      shouldShowGuestPriceComparison,
      hasCustomerGroupPriceDifference,
    ]
  );

  return <PdpContext.Provider value={value}>{children}</PdpContext.Provider>;
};

export const usePdp = () => {
  const context = React.useContext(PdpContext);
  if (context === undefined) {
    throw new Error(`usePdp must be used within a PdpProvider`);
  }
  return context;
};
